import { StateCreator } from 'zustand';
import { BlockchainActions, GalaxyStore, BlockState, TransactionState, Vector3 } from '../types';
import { Block, Transaction } from '../../models';

export interface BlockchainSlice extends BlockchainActions {}

const MAX_BLOCKS = 10; // Keep last 10 blocks as planets

function generateOrbitRadius(index: number): number {
  return 50 + (index * 15); // Start at 50 units, 15 units apart
}

function generatePlanetId(height: number): string {
  return `planet-${height}`;
}

function generateShipId(txId: string): string {
  return `ship-${txId}`;
}

function calculateSpeed(feeRate: number): number {
  // Higher fee rate = faster speed
  const baseSpeed = 1;
  const speedMultiplier = Math.log10(feeRate + 1) + 1;
  return baseSpeed * speedMultiplier;
}

export const createBlockchainSlice: StateCreator<
  GalaxyStore,
  [],
  [],
  BlockchainSlice
> = (set) => ({
  // Actions only - state is defined in main store
  addBlock: (block: Block) => set((state) => {
    const newBlocks = new Map(state.blockchain.blocks);
    const blocks = Array.from(newBlocks.values()).sort((a, b) => b.height - a.height);
    
    // Create new block state
    const blockState: BlockState = {
      ...block,
      planetId: generatePlanetId(block.height),
      orbitRadius: generateOrbitRadius(blocks.length),
      rotationSpeed: 0.001 + (Math.random() * 0.002), // Random rotation speed
      surfaceActivity: block.transactionCount / 3000, // Normalized activity
      feeDistribution: [], // Would be populated from actual data
      transactionTypes: new Map(),
      addedAt: Date.now(),
    };

    // Update current height in network state
    const currentHeight = Math.max(state.network.currentHeight, block.height);

    // Add new block
    newBlocks.set(block.height, blockState);

    // Remove oldest blocks if we exceed MAX_BLOCKS
    if (newBlocks.size > MAX_BLOCKS) {
      const sortedHeights = Array.from(newBlocks.keys()).sort((a, b) => a - b);
      const toRemove = sortedHeights.slice(0, newBlocks.size - MAX_BLOCKS);
      
      toRemove.forEach(height => {
        newBlocks.delete(height);
      });
    }

    return {
      ...state,
      blockchain: {
        ...state.blockchain,
        blocks: newBlocks,
      },
      network: {
        ...state.network,
        currentHeight: currentHeight,
      },
    };
  }),

  removeOldestBlock: () => set((state) => {
    if (state.blockchain.blocks.size === 0) return state;

    const oldestHeight = Math.min(...Array.from(state.blockchain.blocks.keys()));
    const newBlocks = new Map(state.blockchain.blocks);
    newBlocks.delete(oldestHeight);
    
    return {
      ...state,
      blockchain: {
        ...state.blockchain,
        blocks: newBlocks,
      },
    };
  }),

  updateBlockTransactions: (height: number, count: number) => set((state) => {
    const block = state.blockchain.blocks.get(height);
    
    if (!block) return state;
    
    const newBlocks = new Map(state.blockchain.blocks);
    const updatedBlock = {
      ...block,
      transactionCount: count,
      surfaceActivity: count / 3000,
    };
    newBlocks.set(height, updatedBlock);
    
    return {
      ...state,
      blockchain: {
        ...state.blockchain,
        blocks: newBlocks,
      },
    };
  }),

  addTransaction: (tx: Transaction) => set((state) => {
    // Phase 1: Create transaction state starting from outer edge
    const txState: TransactionState = {
      ...tx,
      shipId: generateShipId(tx.id),
      currentPosition: { x: 0, y: 0, z: 0 }, // Will be set by spawn positioning
      targetPosition: { x: 0, y: 0, z: 0 }, // Mempool nebula center initially
      trajectory: {
        startPoint: { x: 0, y: 0, z: 0 },
        endPoint: { x: 0, y: 0, z: 0 },
        duration: 0,
      },
      speed: calculateSpeed(tx.feeRate),
      progress: 0,
      engineGlow: 1,
      trailIntensity: tx.fee / 10000, // Normalized trail intensity
      spawnedAt: Date.now(),
      // Phase 1: Enhanced transaction lifecycle
      lifecycleStatus: 'broadcasting',
      mempoolPosition: undefined,
      selectedBy: undefined,
      targetBlock: undefined,
    };

    const newTransactions = new Map(state.blockchain.transactions);
    newTransactions.set(tx.id, txState);
    
    const newActiveShips = new Set(state.scene.activeShips);
    newActiveShips.add(txState.shipId);
    
    return {
      ...state,
      blockchain: {
        ...state.blockchain,
        transactions: newTransactions,
      },
      scene: {
        ...state.scene,
        activeShips: newActiveShips,
      },
    };
  }),

  confirmTransaction: (txId: string, blockHeight: number) => set((state) => {
    const tx = state.blockchain.transactions.get(txId);
    
    if (!tx) return state;

    // Find the target planet
    const targetBlock = state.blockchain.blocks.get(blockHeight);
    if (!targetBlock) return state;

    // Calculate target position based on planet's orbit
    const angle = (Date.now() / 10000) % (2 * Math.PI);
    const targetPosition: Vector3 = {
      x: Math.cos(angle) * targetBlock.orbitRadius,
      y: 0,
      z: Math.sin(angle) * targetBlock.orbitRadius,
    };

    // Update transaction state
    const updatedTx: TransactionState = {
      ...tx,
      status: 'confirmed',
      confirmations: 1,
      blockHeight: blockHeight,
      targetPosition: targetPosition,
      trajectory: {
        startPoint: tx.currentPosition,
        endPoint: targetPosition,
        duration: 5000 / tx.speed, // Duration based on speed
      },
    };

    const newTransactions = new Map(state.blockchain.transactions);
    newTransactions.set(txId, updatedTx);
    
    return {
      ...state,
      blockchain: {
        ...state.blockchain,
        transactions: newTransactions,
      },
    };
  }),

  removeTransaction: (txId: string) => set((state) => {
    const tx = state.blockchain.transactions.get(txId);
    
    if (!tx) return state;

    const newTransactions = new Map(state.blockchain.transactions);
    newTransactions.delete(txId);
    
    const newActiveShips = new Set(state.scene.activeShips);
    newActiveShips.delete(tx.shipId);
    
    return {
      ...state,
      blockchain: {
        ...state.blockchain,
        transactions: newTransactions,
      },
      scene: {
        ...state.scene,
        activeShips: newActiveShips,
      },
    };
  }),

  updateTransactionLifecycle: (txId: string, status: 'broadcasting' | 'approaching-mempool' | 'in-mempool' | 'selected' | 'pending-inclusion' | 'confirmed', mempoolPosition?: Vector3) => set((state) => {
    const tx = state.blockchain.transactions.get(txId);
    
    if (!tx) return state;

    const updatedTx: TransactionState = {
      ...tx,
      lifecycleStatus: status,
      mempoolPosition: mempoolPosition || tx.mempoolPosition,
    };

    const newTransactions = new Map(state.blockchain.transactions);
    newTransactions.set(txId, updatedTx);
    
    return {
      ...state,
      blockchain: {
        ...state.blockchain,
        transactions: newTransactions,
      },
    };
  }),
});